/**
 * community-intelligence/aggregation.ts — pure aggregation of community knowledge.
 *
 * Everything here is a pure function of (contributions, weights, now) —
 * deterministic and individually testable. Aggregates per-target community
 * ratings, structured facts, community signal tags, local insights, and the
 * mandatory plain-English explanation.
 */

import type {
  CommunityContribution,
  CommunityExplanation,
  CommunitySignalTag,
  LocalInsight,
  TargetIntelligence,
} from './models';

const DAY = 24 * 60 * 60 * 1000;

/** Resolves a contribution's aggregation weight (injected by the engine). */
export type WeightResolver = (contribution: CommunityContribution) => number;

interface WeightedGroup {
  contributions: CommunityContribution[];
  totalWeight: number;
  verifiedWeight: number;
  verifiedCount: number;
}

function groupWeights(contributions: CommunityContribution[], weightOf: WeightResolver): WeightedGroup {
  let totalWeight = 0;
  let verifiedWeight = 0;
  let verifiedCount = 0;
  for (const c of contributions) {
    const w = weightOf(c);
    totalWeight += w;
    if (c.tier === 'verified') {
      verifiedWeight += w;
      verifiedCount += 1;
    }
  }
  return { contributions, totalWeight, verifiedWeight, verifiedCount };
}

function weightedRating(contributions: CommunityContribution[], weightOf: WeightResolver): number | null {
  let sum = 0;
  let weight = 0;
  for (const c of contributions) {
    if (c.rating == null) continue;
    const w = weightOf(c);
    sum += c.rating * w;
    weight += w;
  }
  return weight > 0 ? Math.round((sum / weight) * 10) / 10 : null;
}

/** Aggregate structured facts: numeric → weighted mean, string → dominant option. */
export function aggregateFacts(
  contributions: CommunityContribution[],
  weightOf: WeightResolver,
): Record<string, string | number> {
  const numeric = new Map<string, { sum: number; weight: number }>();
  const categorical = new Map<string, Map<string, number>>();
  for (const c of contributions) {
    const w = weightOf(c);
    for (const [key, value] of Object.entries(c.attributes)) {
      if (typeof value === 'number') {
        const agg = numeric.get(key) ?? { sum: 0, weight: 0 };
        agg.sum += value * w;
        agg.weight += w;
        numeric.set(key, agg);
      } else if (typeof value === 'string') {
        const options = categorical.get(key) ?? new Map<string, number>();
        options.set(value, (options.get(value) ?? 0) + w);
        categorical.set(key, options);
      }
    }
  }
  const facts: Record<string, string | number> = {};
  for (const [key, { sum, weight }] of numeric) {
    if (weight > 0) facts[key] = Math.round((sum / weight) * 10) / 10;
  }
  for (const [key, options] of categorical) {
    let best: string | null = null;
    let bestW = 0;
    for (const [option, w] of options) {
      if (w > bestW) { best = option; bestW = w; }
    }
    if (best != null) facts[key] = best;
  }
  return facts;
}

// ─── Community signal tags ─────────────────────────────────────────────────────

export function computeSignals(
  contributions: CommunityContribution[],
  group: WeightedGroup,
  rating: number | null,
  now: number,
  /** Highest recommendation score among sibling targets (for mostRecommended). */
  maxSiblingScore: number,
  ownScore: number,
): CommunitySignalTag[] {
  const signals: CommunitySignalTag[] = [];
  const count = contributions.length;
  const first = contributions.reduce((min, c) => Math.min(min, c.at), Infinity);
  const recent14 = contributions.filter((c) => now - c.at <= 14 * DAY).length;
  const prior14 = contributions.filter((c) => now - c.at > 14 * DAY && now - c.at <= 28 * DAY).length;

  if (count > 0 && ownScore >= maxSiblingScore && ownScore > 0) signals.push('mostRecommended');
  if (recent14 >= 3 && recent14 > prior14) signals.push('trending');
  if (rating != null && rating >= 4.5 && count >= 2 && count <= 5) signals.push('hiddenGem');

  // Improved/declined: recent 90d weighted rating vs the 90d before that.
  const recentRated = contributions.filter((c) => c.rating != null && now - c.at <= 90 * DAY);
  const olderRated = contributions.filter((c) => c.rating != null && now - c.at > 90 * DAY && now - c.at <= 180 * DAY);
  if (recentRated.length >= 2 && olderRated.length >= 2) {
    const avg = (list: CommunityContribution[]) => list.reduce((s, c) => s + (c.rating ?? 0), 0) / list.length;
    const delta = avg(recentRated) - avg(olderRated);
    if (delta >= 0.5) signals.push('recentlyImproved');
    if (delta <= -0.5) signals.push('recentlyDeclined');
  }

  if (count >= 5 && group.totalWeight > 0 && group.verifiedWeight / group.totalWeight >= 0.7) {
    signals.push('highlyTrusted');
  }
  if (Number.isFinite(first) && now - first <= 30 * DAY) signals.push('newDiscovery');
  if (count >= 10 && rating != null && rating >= 4) signals.push('communityFavorite');
  return signals;
}

// ─── Explanations (mandatory) ──────────────────────────────────────────────────

const FACT_DESCRIPTIONS: Record<string, (v: string | number) => string> = {
  internetMbps: (v) => `Average internet speed: ${v} Mbps.`,
  noiseLevel: (v) => `${v === 'quiet' ? 'Quiet' : `Reported ${v}`} work environment.`,
  powerReliability: (v) => `Power reliability: ${v}.`,
  monthlyCost: (v) => `Typical monthly cost: $${v}.`,
  safetyRating: (v) => `Community safety rating: ${v}/5.`,
};

export function buildExplanation(
  group: WeightedGroup,
  rating: number | null,
  facts: Record<string, string | number>,
): CommunityExplanation {
  const reasons: string[] = [];
  if (group.verifiedCount > 0) {
    reasons.push(`${group.verifiedCount} verified WorkRoamer${group.verifiedCount === 1 ? '' : 's'} recommend this location.`);
  }
  const anonymous = group.contributions.length - group.verifiedCount;
  if (anonymous > 0 && group.verifiedCount === 0) {
    reasons.push(`Based on ${anonymous} community report${anonymous === 1 ? '' : 's'}.`);
  }
  for (const [key, describe] of Object.entries(FACT_DESCRIPTIONS)) {
    if (key in facts) reasons.push(describe(facts[key]));
  }
  if (rating != null) {
    reasons.push(rating >= 4.5 ? 'Excellent community rating.' : `Community rating: ${rating}/5.`);
  }
  return {
    summary: `Aggregated from ${group.contributions.length} community contribution${group.contributions.length === 1 ? '' : 's'}.`,
    reasons,
  };
}

// ─── Target + local-insight aggregation ────────────────────────────────────────

/** Recommendation score used for ranking targets within a location. */
export function recommendationScore(group: WeightedGroup, rating: number | null): number {
  return (rating ?? 2.5) * group.totalWeight;
}

export function aggregateTarget(
  targetId: string,
  contributions: CommunityContribution[],
  weightOf: WeightResolver,
  now: number,
  maxSiblingScore: number,
): TargetIntelligence {
  const group = groupWeights(contributions, weightOf);
  const rating = weightedRating(contributions, weightOf);
  const facts = aggregateFacts(contributions, weightOf);
  const ownScore = recommendationScore(group, rating);
  const sample = contributions[0];
  return {
    targetId,
    targetLabel: sample?.targetLabel ?? targetId,
    locationId: sample?.locationId ?? '',
    communityRating: rating,
    contributionCount: contributions.length,
    verifiedCount: group.verifiedCount,
    verifiedWeightShare: group.totalWeight > 0 ? Math.round((group.verifiedWeight / group.totalWeight) * 100) / 100 : 0,
    facts,
    signals: computeSignals(contributions, group, rating, now, maxSiblingScore, ownScore),
    explanation: buildExplanation(group, rating, facts),
    updatedAt: now,
  };
}

/**
 * Local knowledge: contributions tagged with a `topic` attribute compete per
 * (location, topic); the highest weighted-scoring target wins the insight
 * ("Best café before 10 AM" → topic 'best-cafe-before-10am').
 */
export function aggregateLocalInsights(
  contributions: CommunityContribution[],
  weightOf: WeightResolver,
  locationId: string,
): LocalInsight[] {
  const byTopic = new Map<string, Map<string, CommunityContribution[]>>();
  for (const c of contributions) {
    if (c.locationId !== locationId) continue;
    const topic = c.attributes['topic'];
    if (typeof topic !== 'string') continue;
    const targets = byTopic.get(topic) ?? new Map<string, CommunityContribution[]>();
    const list = targets.get(c.targetId) ?? [];
    list.push(c);
    targets.set(c.targetId, list);
    byTopic.set(topic, targets);
  }
  const insights: LocalInsight[] = [];
  for (const [topic, targets] of byTopic) {
    let best: { targetId: string; list: CommunityContribution[]; score: number } | null = null;
    for (const [targetId, list] of targets) {
      const group = groupWeights(list, weightOf);
      const score = recommendationScore(group, weightedRating(list, weightOf));
      if (!best || score > best.score) best = { targetId, list, score };
    }
    if (!best) continue;
    const group = groupWeights(best.list, weightOf);
    insights.push({
      locationId,
      topic,
      targetId: best.targetId,
      targetLabel: best.list[0].targetLabel,
      score: Math.round(best.score * 10) / 10,
      explanation: buildExplanation(group, weightedRating(best.list, weightOf), aggregateFacts(best.list, weightOf)),
    });
  }
  return insights.sort((a, b) => a.topic.localeCompare(b.topic));
}
